/**
 * Side panel renderer (vocabulary sp@2), ported from the spec's reference renderer.js.
 *
 * Adds what authors never write:
 *  - Save % label in .sp-price, computed from <del> and <b>
 *  - five stars + score before the .sp-rating text, from data-score
 *  - an inline stroke icon at the start of each li[data-icon]
 *  - the name / detail split of a quote <cite> ("Name · detail")
 *  - data-sp-accent="amber|ink" on each .sp-cta; in PRISM at most one amber CTA per page
 * Generated nodes are marked data-sp="…" so the skins can style them.
 */
import { DesignLanguage, SidePanelCard } from '../../models/side-panel-card';
import { SIDE_PANEL_MAX_CARDS } from './side-panel-config';
import { guardCardHtml } from './sp-guard';

// Stand-ins for the PRiSM stroke set, drawn in the Lucide style. The names are the contract, not the drawings.
const ICONS: { [name: string]: string } = {
  ClipboardCheck: '<rect x="8" y="2.5" width="8" height="4" rx="1"/>' +
    '<path d="M16 4.5h2a2 2 0 0 1 2 2V20a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6.5a2 2 0 0 1 2-2h2"/>' +
    '<path d="m9 14 2 2 4-4"/>',
  Sections: '<rect x="3.5" y="4" width="7" height="16" rx="1.5"/>' +
    '<rect x="13.5" y="4" width="7" height="16" rx="1.5"/>',
  Book: '<path d="M2 4h6a4 4 0 0 1 4 4v13a3 3 0 0 0-3-3H2z"/>' +
    '<path d="M22 4h-6a4 4 0 0 0-4 4v13a3 3 0 0 1 3-3h7z"/>',
  Play: '<path d="M7 4.5v15a.8.8 0 0 0 1.2.7l12-7.5a.8.8 0 0 0 0-1.4l-12-7.5A.8.8 0 0 0 7 4.5z"/>',
  Timer: '<path d="M10 2h4"/><path d="m12 14 3-3"/><circle cx="12" cy="14" r="8"/>',
  Target: '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>',
  Trophy: '<path d="M7 4h10v5a5 5 0 0 1-10 0z"/>' +
    '<path d="M7 6H4.5a2.5 2.5 0 0 0 2.5 4"/><path d="M17 6h2.5a2.5 2.5 0 0 1-2.5 4"/>' +
    '<path d="M12 14v4"/><path d="M8 21h8"/><path d="M9.5 18h5v3h-5z"/>',
  TrendingUp: '<path d="M2 17 8.5 10.5l5 5L22 7"/><path d="M16 7h6v6"/>',
  Star: '<path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4l-5.9 3.1 1.2-6.5L2.5 9.4l6.6-.9z" fill="currentColor"/>',
  Pace: '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6.5"/><circle cx="12" cy="12" r="3"/>' +
    '<circle cx="12" cy="12" r=".6" fill="currentColor"/>',
  Sparkle: '<path d="M10 3.5c.6 3.9 2.6 5.9 6.5 6.5-3.9.6-5.9 2.6-6.5 6.5-.6-3.9-2.6-5.9-6.5-6.5 3.9-.6 5.9-2.6 6.5-6.5z"/>' +
    '<path d="M18.5 3v4"/><path d="M20.5 5h-4"/><path d="M18 16.5v3"/><path d="M19.5 18h-3"/>',
  Bell: '<path d="M6 8.5a6 6 0 0 1 12 0c0 6.5 2.5 8.5 2.5 8.5h-17S6 15 6 8.5z"/>' +
    '<path d="M10.3 21a2 2 0 0 0 3.4 0"/>',
  Shield: '<path d="M12 22s8-3.8 8-10V5.5L12 2.5 4 5.5V12c0 6.2 8 10 8 10z"/>',
  Lock: '<rect x="4" y="11" width="16" height="10.5" rx="2"/><path d="M8 11V7.5a4 4 0 0 1 8 0V11"/>',
  CheckCircle: '<path d="M21.8 10.9A10 10 0 1 1 16 3"/><path d="m9 11 3 3L22 4"/>'
};

// Greyscale placeholders from the spec, used when an image or avatar fails to load.
function svgUri(svg: string): string {
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}
const PLACEHOLDER = {
  scene: svgUri(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 180" preserveAspectRatio="xMidYMid slice">' +
    '<rect width="320" height="180" fill="#D9D9D9"/><circle cx="238" cy="54" r="20" fill="#F2F2F2"/>' +
    '<path d="M0 132 70 84l52 36 58-52 64 50 76-44v106H0z" fill="#AEAEAE"/>' +
    '<path d="M0 150 88 112l70 30 82-28 80 26v40H0z" fill="#8F8F8F"/></svg>'),
  portrait: svgUri(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 240" preserveAspectRatio="xMidYMid slice">' +
    '<rect width="320" height="240" fill="#D6D6D6"/><circle cx="160" cy="98" r="40" fill="#8E8E8E"/>' +
    '<path d="M76 240c4-50 40-86 84-86s80 36 84 86z" fill="#7A7A7A"/></svg>'),
  avatar: svgUri(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96"><rect width="96" height="96" fill="#D6D6D6"/>' +
    '<circle cx="48" cy="38" r="16" fill="#8E8E8E"/><path d="M16 96c2-22 15-36 32-36s30 14 32 36z" fill="#7A7A7A"/></svg>')
};

function useFallbackOnError(img: HTMLImageElement, fallback: string): void {
  img.addEventListener('error', function onError() {
    img.removeEventListener('error', onError);
    img.setAttribute('src', fallback);
  });
}

const STAR_PATH ='M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4l-5.9 3.1 1.2-6.5L2.5 9.4l6.6-.9z';
let starSeq = 0;

function iconSvg(name: string): string {
  const body = ICONS[name];
  if (!body) {
    return '';
  }
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" ' +
    'aria-hidden="true" focusable="false">' + body + '</svg>';
}

function starSvg(fill: number): string {
  const open = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">';
  if (fill >= 1) {
    return open + '<path d="' + STAR_PATH + '" fill="currentColor"/></svg>';
  }
  const outline = '<path d="' + STAR_PATH + '" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>';
  if (fill <= 0) {
    return open + outline + '</svg>';
  }
  const id = 'sp-half-' + (++starSeq);
  return open + '<defs><clipPath id="' + id + '"><rect x="0" y="0" width="12" height="24"/></clipPath></defs>' +
    outline + '<path d="' + STAR_PATH + '" fill="currentColor" clip-path="url(#' + id + ')"/></svg>';
}

function generated(tag: string, spName: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  node.setAttribute('data-sp', spName);
  if (text != null) {
    node.textContent = text;
  }
  return node;
}

function parseAmount(value: string): number {
  const match = String(value || '').replace(/,/g, '').match(/\d+(?:\.\d+)?/);
  return match ? parseFloat(match[0]) : NaN;
}

function each(root: Element, selector: string, fn: (el: Element) => void): void {
  Array.prototype.forEach.call(root.querySelectorAll(selector), fn);
}

function enhance(card: HTMLElement): void {
  // Images: a missing file shows a neutral greyscale placeholder, never a broken-image icon.
  each(card, 'img.sp-image', img => useFallbackOnError(img as HTMLImageElement,
    img.getAttribute('data-ratio') === '4:3' ? PLACEHOLDER.portrait : PLACEHOLDER.scene));
  each(card, '.sp-person img', img => useFallbackOnError(img as HTMLImageElement, PLACEHOLDER.avatar));

  // Links: the button, the link below it and inline body links all open in a new tab, as the old notifications did.
  each(card, 'a[href]', link => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });

  each(card, '.sp-price', price => {
    if (price.querySelector('[data-sp="save"]')) {
      return;
    }
    const oldPrice = parseAmount(price.querySelector('del') ? price.querySelector('del').textContent : '');
    const newPrice = parseAmount(price.querySelector('b') ? price.querySelector('b').textContent : '');
    if (oldPrice > 0 && newPrice >= 0 && newPrice < oldPrice) {
      const pct = Math.round((oldPrice - newPrice) / oldPrice * 100);
      price.appendChild(generated('span', 'save', 'Save ' + pct + '%'));
    }
  });

  each(card, '.sp-rating', rating => {
    if (rating.querySelector('[data-sp="stars"]')) {
      return;
    }
    let score = parseFloat(rating.getAttribute('data-score'));
    if (isNaN(score)) {
      score = 0;
    }
    score = Math.max(0, Math.min(5, score));
    const rounded = Math.round(score * 2) / 2;
    const count = generated('span', 'count');
    while (rating.firstChild) {
      count.appendChild(rating.firstChild);
    }
    count.textContent = count.textContent.trim();
    const stars = generated('span', 'stars');
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', score.toFixed(1) + ' out of 5');
    let starsHtml = '';
    for (let i = 0; i < 5; i++) {
      starsHtml += starSvg(Math.max(0, Math.min(1, rounded - i)));
    }
    stars.innerHTML = starsHtml;
    rating.appendChild(stars);
    rating.appendChild(generated('b', 'score', score.toFixed(1)));
    if (count.textContent) {
      rating.appendChild(count);
    }
  });

  each(card, '.sp-iconlist > li[data-icon]', item => {
    if (item.querySelector('[data-sp="icon"]')) {
      return;
    }
    const text = generated('span', 'text');
    while (item.firstChild) {
      text.appendChild(item.firstChild);
    }
    const icon = generated('span', 'icon');
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = iconSvg(item.getAttribute('data-icon'));
    item.appendChild(icon);
    item.appendChild(text);
  });

  each(card, 'blockquote.sp-quote', quote => {
    if (quote.querySelector('[data-sp="qtext"]')) {
      return;
    }
    const cite = quote.querySelector('cite');
    const quoteText = generated('span', 'qtext');
    Array.prototype.slice.call(quote.childNodes).forEach((node: Node) => {
      if (node !== cite) {
        quoteText.appendChild(node);
      }
    });
    quoteText.innerHTML = quoteText.innerHTML.trim();
    quote.insertBefore(quoteText, cite || null);
    if (cite) {
      const citeText = cite.textContent.trim();
      const separator = citeText.indexOf('·');
      cite.textContent = '';
      if (separator > 0) {
        cite.appendChild(generated('b', 'name', citeText.slice(0, separator).trim()));
        cite.appendChild(document.createTextNode(' '));
        cite.appendChild(generated('span', 'detail', citeText.slice(separator).trim()));
      } else {
        cite.appendChild(generated('b', 'name', citeText));
      }
    }
  });
}

// Amber if data-emphasis="high", or if emphasis is unset and the card carries an urgent chip.
function wantsAmber(card: HTMLElement): boolean {
  const cta = card.querySelector('.sp-cta');
  if (!cta) {
    return false;
  }
  const emphasis = cta.getAttribute('data-emphasis');
  if (emphasis === 'high') {
    return true;
  }
  if (emphasis === 'normal') {
    return false;
  }
  return !!card.querySelector('.sp-chip[data-tone="urgent"]');
}

function applyAccents(cards: HTMLElement[], language: DesignLanguage): void {
  let amberUsed = false;
  cards.forEach(card => {
    const cta = card.querySelector('.sp-cta');
    if (!cta) {
      return;
    }
    let amber = wantsAmber(card);
    if (language === 'PRISM') {
      if (amber && amberUsed) {
        amber = false;
      }
      if (amber) {
        amberUsed = true;
      }
    }
    cta.setAttribute('data-sp-accent', amber ? 'amber' : 'ink');
  });
}

/**
 * Paints up to two cards into the container in order. Both cards use card 1's design language.
 *
 * @returns number of cards drawn
 */
export function renderCards(container: HTMLElement, cards: SidePanelCard[]): number {
  // Cards without an <aside class="sp"> (old-format notifications) are skipped and don't use up a slot.
  const drawable: { card: SidePanelCard, aside: HTMLElement }[] = [];
  (cards || []).forEach(card => {
    const aside = guardCardHtml(card.html)[0];
    if (aside && drawable.length < SIDE_PANEL_MAX_CARDS) {
      drawable.push({ card, aside });
    }
  });
  const language: DesignLanguage = drawable.length && drawable[0].card.designLanguage === 'PRISM' ? 'PRISM' : 'CLASSIC';
  container.classList.remove('sp-lang-classic', 'sp-lang-prism');
  container.classList.add(language === 'PRISM' ? 'sp-lang-prism' : 'sp-lang-classic');
  container.innerHTML = '';

  const drawn: HTMLElement[] = [];
  drawable.forEach(item => {
    enhance(item.aside);
    drawn.push(item.aside);
    container.appendChild(item.aside);
  });
  applyAccents(drawn, language);
  return drawn.length;
}
